import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  DATA_FLAG_BASIS_OPTIONS,
  submitEdLocationDataFlag,
} from "@healthdesk/shared-services";
import { isEmergencyDepartmentLocation } from "../../constants/diagnosticCapabilities";

/**
 * Tier 1 — report incorrect facility information (any signed-in user).
 */
export default function EdDataFlagPanel({ location, locationId, onSubmitted }) {
  const [basis, setBasis] = useState("");
  const [specifics, setSpecifics] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submitFlag = async () => {
    setError("");
    if (!basis) {
      setError("Please select what information may be incorrect.");
      return;
    }
    if (specifics.trim().length < 10) {
      setError("Please describe the issue in at least 10 characters.");
      return;
    }

    setLoading(true);
    try {
      await submitEdLocationDataFlag({
        locationId,
        basis,
        specifics: specifics.trim(),
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      console.warn("[EdDataFlagPanel] submit failed:", err);
      setError(err?.message || "Unable to submit report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Thank you. Your report has been submitted for review. No changes were made to this
        listing.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Report incorrect information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If something about this emergency department looks wrong, tell us what needs correction.
        This does not change the listing — it opens a review.
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="data-flag-basis-label">What may be incorrect?</InputLabel>
        <Select
          labelId="data-flag-basis-label"
          value={basis}
          label="What may be incorrect?"
          onChange={(e) => setBasis(e.target.value)}
        >
          {DATA_FLAG_BASIS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label="Details (required)"
        placeholder="Describe what is wrong and any correct information you know."
        value={specifics}
        onChange={(e) => setSpecifics(e.target.value)}
        sx={{ mb: 2 }}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      <Button variant="outlined" size="small" onClick={submitFlag} disabled={loading}>
        {loading ? "Submitting…" : "Submit report"}
      </Button>
    </Box>
  );
}

/**
 * @param {object | null | undefined} location
 * @param {boolean} isLoggedIn
 */
export function canShowEdDataFlagPanel(location, isLoggedIn) {
  return Boolean(isLoggedIn && location && isEmergencyDepartmentLocation(location));
}
